import { ManageSearch } from "@mui/icons-material";
import { Box, Typography, useMediaQuery } from "@mui/material";
import { QueryErrorResetBoundary } from "@tanstack/react-query";
import { useSetAtom } from "jotai";
import type React from "react";
import { use } from "react";
import { Suspense, useMemo } from "react";
import { ErrorBoundary } from "react-error-boundary";
import * as v from "valibot";

import type { GetApiAssetsadvertMapParams } from "../../../api/rest/generated/aPIForPublicPartOfApplicationInzerceNemovitéhoMajetku.schemas.ts";
import {
  useGetApiAssetsadvertAdvertisements,
  useGetApiAssetsadvertMap,
} from "../../../api/rest/generated/assets-advert/assets-advert.ts";
import { FETCH_INVALIDATION_QUERY_KEYS } from "../../../api/rest/queryKeys.ts";
import type { OverviewForm } from "../../../hooks/useOverview.ts";
import { resultsSchema } from "../../../hooks/useOverview.ts";
import ContentPaper from "../../../Layouts/sz-content-paper/ContentPaper.tsx";
import getPendingMessage from "../../../utills/getPendingMessage.ts";
import FetchingErrorComponent from "../FetchingErrorComponent.tsx";
import SzBackdrop from "../UISharedConstructors/Loading/SzBackDrop.tsx";
import { initialCounts, regionsCountAtom } from "./CzechMap.tsx";
import { ResultsList } from "./results/ResultsList.tsx";
import { ResultsPagination, totalCountAtom, } from "./results/ResultsPagination.tsx";
import SortFilter from "./results/SortFilter.tsx";
import type { useHomeFilter } from "../../../hooks/useHomeFilter.ts";
import { isEvyveska } from "../../../utills/iframeVariants.ts";

export const Results: React.FC<{ overviewForm: OverviewForm, searchFilter: ReturnType<typeof useHomeFilter>['parsedFilter'] }> = ({
  overviewForm,
  searchFilter,
}) => {
  const setCounts = useSetAtom(regionsCountAtom);
  const setTotalCount = useSetAtom(totalCountAtom);

  const searchFilterCleaned = {
    Id: searchFilter.Id === 0 ? undefined : searchFilter.Id,
    AssetsCategoryId:
      searchFilter.AssetsCategoryId === 0
        ? undefined
        : searchFilter.AssetsCategoryId,
    Price: searchFilter.Price === 0 ? undefined : searchFilter.Price,
    RegionId: searchFilter.RegionId === 0 ? undefined : searchFilter.RegionId,
    DetailCategoryFilter: {
      NajetoDo:
        searchFilter.DetailCategoryFilter.NajetoDo === ""
          ? undefined
          : searchFilter.DetailCategoryFilter.NajetoDo,
      Palivo:
        searchFilter.DetailCategoryFilter.Palivo === ""
          ? undefined
          : searchFilter.DetailCategoryFilter.Palivo,
      // Prevodovka:
      //   searchFilter.DetailCategoryFilter.Prevodovka === ""
      //     ? undefined
      //     : searchFilter.DetailCategoryFilter.Prevodovka,
      // RokVyrobyOd:
      //   searchFilter.DetailCategoryFilter.RokVyrobyOd === ""
      //     ? undefined
      //     : searchFilter.DetailCategoryFilter.RokVyrobyOd,
      // RokVyrobyDo:
      //   searchFilter.DetailCategoryFilter.RokVyrobyDo === ""
      //     ? undefined
      //     : searchFilter.DetailCategoryFilter.RokVyrobyDo,
      RokUvedeniDoProvozuOd:
        searchFilter.DetailCategoryFilter.RokUvedeniDoProvozuOd === ""
          ? undefined
          : searchFilter.DetailCategoryFilter.RokUvedeniDoProvozuOd,
      RokUvedeniDoProvozuDo:
        searchFilter.DetailCategoryFilter.RokUvedeniDoProvozuDo === ""
          ? undefined
          : searchFilter.DetailCategoryFilter.RokUvedeniDoProvozuDo,
      Znacka:
        searchFilter.DetailCategoryFilter.Znacka === ""
          ? undefined
          : searchFilter.DetailCategoryFilter.Znacka,
    },
    sort: searchFilter.sort,
    pageSize: searchFilter.pageSize,
    page: searchFilter.page,
  };

  const isSmall = useMediaQuery("(max-width:600px)");

  const parsedFilter = v.safeParse(resultsSchema, searchFilterCleaned);

  // Update the map counts
  useGetApiAssetsadvertMap(searchFilterCleaned, {
    query: {
      queryKey: [FETCH_INVALIDATION_QUERY_KEYS.map, searchFilterCleaned],
      select: (data) => {
        const newCounts = {
          ...initialCounts,
          ...data,
        };

        setCounts(newCounts);
        setTotalCount(data.totalRecords ?? 0);
      },
    },
  });

  // Update results
  const resultsQuery = useGetApiAssetsadvertAdvertisements(
    searchFilterCleaned as GetApiAssetsadvertMapParams,
    {
      query: {
        queryKey: [FETCH_INVALIDATION_QUERY_KEYS.results, searchFilterCleaned],
      },
    },
  );

  const resultsMessage = useMemo(
    () =>
      getPendingMessage({
        pendingMessage: "načítám inzeráty",
        failureReason: resultsQuery.failureReason,
        failureCount: resultsQuery.failureCount,
        isFetching: resultsQuery.isFetching,
      }),
    [
      resultsQuery.isFetching,
      resultsQuery.failureReason,
      resultsQuery.failureCount,
    ],
  );

  const totalRecords = resultsQuery.data?.totalRecords ?? 0;
  // use(resultsQuery.promise);

  return (
    <>
      <ContentPaper
        sx={{
          boxShadow: totalRecords > 0? undefined: 'none' // remove bottom border when no results
        }}
      >
        <ContentPaper.Header icon={<ManageSearch />} title="Výsledky">
          {!isSmall && <SortFilter overviewForm={overviewForm} />}
        </ContentPaper.Header>
        <ContentPaper.Body
          id="results"
          sx={{
            containerName: "results",
            containerType: "inline-size",
            gap: "2em",
          }}
        >
          {isSmall && (
            <SortFilter
              variant="contained"
              fullwidth={true}
              overviewForm={overviewForm}
            />
          )}
          <QueryErrorResetBoundary>
            {({ reset }) => {

              // If user has wrong input and it results in error, even when he fixes input the error message stays.
              // So we need to reset error boundary when new data is fetched ( https://stackoverflow.com/questions/71762507/reset-error-boundary-on-back-journey-using-useerrorhandler-hook-from-react-error )
              const errorBoundaryKey = `error-boundary-key|${JSON.stringify(resultsQuery.queryKey)}`;

              return (
              <ErrorBoundary
                key={errorBoundaryKey}
                onReset={reset}
                fallbackRender={({ resetErrorBoundary }) => {
                  if (parsedFilter.success) {
                    resetErrorBoundary();
                  }
                  return (
                    <FetchingErrorComponent
                      statusCode={resultsQuery.error?.status ?? 0}
                    />
                  );
                }}
              >
                <Suspense
                  fallback={
                    <Box height="30em">
                      <SzBackdrop message={resultsMessage} />
                    </Box>
                  }
                >
                  <ResultsList
                    isLoading={resultsQuery.isFetching}
                    overviewForm={overviewForm}
                    resultsPromise={resultsQuery.promise}
                    resultsData={resultsQuery.data}
                  />
                </Suspense>
              </ErrorBoundary>
              )
            }}
          </QueryErrorResetBoundary>
        </ContentPaper.Body>

        {/* TODO: fix this if needed */}
        {/*<ResultsPagination overviewForm={overviewForm} totalCount={resultsQuery.data.totalRecords} />*/}

      </ContentPaper>

      <ExtraScrollSpace totalRecords={totalRecords}/>

      <Typography sx={{ color: "transparent", backgroundColor: "transparent" }}>
        verze: {__APP_VERSION__} {__BUILD_DATE__}
      </Typography>
    </>
  );
};


// it is annoying when user clicks on regions and window can't scroll fully, because only few items are loaded
// this makes sure it can never happen
// This is simpler solution than using dynamic measure, maybe setting height when about to scroll could work too.
function ExtraScrollSpace({totalRecords}: {totalRecords: number}) {

  const extraSpaceHeitghtPX = isEvyveska()? (
    Math.max(0, (
      screen.height
      - 175 // filter height
      // - 160 // "to je bohužel vše" height; You can also find it by search for ITUP-71580
      - (totalRecords * 160)
    ))
  ) : (
    0 // it is not a problem on regular website
  )

  return (
    <div
      style={{
        height: `${extraSpaceHeitghtPX}px`,
        // background:'#f0f',
      }}
    />
  )
}
